import { useContext, useState, useMemo, useEffect, useCallback } from "react";
import type { ReactElement } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import ja from "date-fns/locale/ja";
import enUS from "date-fns/locale/en-US";
import { jaJP, enUS as enUSLocale } from "@mui/x-date-pickers/locales";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";
import MDAlert from "components/MDAlert";

import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";

import { usePaywall } from "context/PaywallContext"; 
import { getUserInfos } from "@shared/api/userInfo";
import { registGoal, updateGoal, getUserGoalCount, getAiPlanStatus } from "@shared/api/aiCoaching";

const generateDefaultTimes = (): [string, string][] => {
  return [
    ["06:00", "07:00"],
    ["07:00", "08:00"],
    ["18:00", "19:00"],
    ["19:00", "20:00"],
    ["20:00", "21:00"],
    ["21:00", "22:00"],
    ["22:00", "23:00"],
  ];
};

function getStepContent(
  stepIndex: number,
  goal: any,
  handleChange: (key: string, value: any) => void,
  weekdayTimes: [string, string | null][],
  setWeekdayTimes: React.Dispatch<React.SetStateAction<[string, string | null][]>>,
  weekendTimes: [string, string | null][],
  setWeekendTimes: React.Dispatch<React.SetStateAction<[string, string | null][]>>,
  weekdayChecked: boolean[],
  setWeekdayChecked: React.Dispatch<React.SetStateAction<boolean[]>>,
  weekendChecked: boolean[],
  setWeekendChecked: React.Dispatch<React.SetStateAction<boolean[]>>,
  t: Function,
  categoryOptions: { label: string; value: string; image: string }[],
  goalTypeOptions: { label: string; value: string }[],
  motivationOptions: { label: string; value: string }[],
  proficiencyOptions: { label: string; value: number }[],
  dateLocale: any,
  customLocaleText: any
): JSX.Element {
  const parseTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes || 0, 0, 0);
    return date;
  };

  const formatTime = (date: Date) =>
    `${date.getHours().toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;

  switch (stepIndex) {
    case 0:
      return (
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {t("AI-Coaching.goalWizard.step.category")}
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            {t("AI-Coaching.goalWizard.form.categoryHelp")}
          </Typography>
          <Grid container spacing={2}>
            {categoryOptions.map((opt) => (
              <Grid item xs={6} sm={4} md={3} key={opt.value}>
                <Box
                  onClick={() => handleChange("category", opt.value)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    aspectRatio: "0.9", 
                    paddingTop: "12px", 
                    paddingLeft: "8px", 
                    paddingRight: "8px", 
                    paddingBottom: "8px", 
                    border: goal.category === opt.value ? "2px solid #4a90e2" : "1px solid #e0e0e0",
                    backgroundColor: goal.category === opt.value ? "rgba(74, 144, 226, 0.05)" : "#ffffff",
                    borderRadius: "12px", 
                    cursor: "pointer",
                    boxSizing: "border-box", 
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: goal.category === opt.value ? "rgba(74, 144, 226, 0.1)" : "#f8f9fa",
                      borderColor: goal.category === opt.value ? "#4a90e2" : "#c0c0c0",
                    }
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" align="center" sx={{ mb: "6px", lineHeight: 1.2 }}>
                    {opt.label}
                  </Typography>
                  <img
                    src={opt.image}
                    alt={opt.label}
                    style={{
                      width: "100%",
                      height: "100%",
                      flex: 1, 
                      objectFit: "cover",
                      borderRadius: "6px", 
                      display: "block",
                      minHeight: 0, 
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      );
    case 1:
      return (
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {t("AI-Coaching.goalWizard.step.detail")}
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            {t("AI-Coaching.goalWizard.form.goalDetailHelp")}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={5}
            label={t("AI-Coaching.goalWizard.form.goalDetail")}
            value={goal.goal}
            onChange={(e) => handleChange("goal", e.target.value)}
          />
        </Grid>
      );
    case 2:
      return (
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {t("AI-Coaching.goalWizard.step.type")}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              {t("AI-Coaching.goalWizard.form.goalTypeHelp")}
            </Typography>
            <Grid container spacing={2}>
              {goalTypeOptions.map((opt) => (
                <Grid item xs={12} sm={6} key={opt.value}>
                  <Box
                    onClick={() => handleChange("goal_type", opt.value)}
                    sx={{
                      p: 2,
                      textAlign: "center",
                      border: goal.goal_type === opt.value ? "2px solid #4a90e2" : "1px solid #e0e0e0",
                      backgroundColor: goal.goal_type === opt.value ? "#eef5fc" : "transparent",
                      borderRadius: 2,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <Typography variant="button" color={goal.goal_type === opt.value ? "#4a90e2" : "textPrimary"}>
                      {opt.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {goal.goal_type === "deadline" && (
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t("AI-Coaching.goalWizard.form.deadlineSetting")}
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={2}>
                {t("AI-Coaching.goalWizard.form.deadlineHelp")}
              </Typography>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={dateLocale}
                localeText={customLocaleText}
              >
                <MDDatePicker
                  input={{
                    label: t("AI-Coaching.goalWizard.form.deadlineLabel"),
                    variant: "outlined",
                    fullWidth: true,
                    InputLabelProps: {
                      shrink: true,
                    },
                  }}
                  value={goal.target_date || null}
                  onChange={(val: Date[]) => {
                    let dateStr = "";
                    if (val?.[0] instanceof Date) {
                      const target = val[0];
                      const year = target.getFullYear();
                      const month = String(target.getMonth() + 1).padStart(2, "0");
                      const day = String(target.getDate()).padStart(2, "0");
                      dateStr = `${year}-${month}-${day}`;
                    }
                    handleChange("target_date", dateStr);
                  }}
                  options={{
                    dateFormat: "Y-m-d",
                    defaultDate: new Date(new Date().setMonth(new Date().getMonth() + 6)),
                    minDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
                    maxDate: new Date(new Date().setMonth(new Date().getMonth() + 36)),
                  }}
                />
              </LocalizationProvider>
            </Grid>
          )}
        </Grid>
      );
    case 3:
      return (
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {t("AI-Coaching.goalWizard.step.motivation")}
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            {t("AI-Coaching.goalWizard.form.motivationHelp")}
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {motivationOptions.map((opt) => (
              <Box
                key={opt.value}
                onClick={() => handleChange("motivationtype", opt.value)}
                sx={{
                  p: 2,
                  border: goal.motivationtype === opt.value ? "2px solid #4a90e2" : "1px solid #e0e0e0",
                  backgroundColor: goal.motivationtype === opt.value ? "#eef5fc" : "transparent",
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <Typography variant="body1" fontWeight={goal.motivationtype === opt.value ? "bold" : "regular"} color={goal.motivationtype === opt.value ? "#4a90e2" : "textPrimary"}>
                  {opt.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      );
    case 4: {
      return (
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {t("AI-Coaching.goalWizard.step.proficiency")}
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            {t("AI-Coaching.goalWizard.form.proficiencyHelp")}
          </Typography>
          <Grid container spacing={2}>
            {proficiencyOptions.map((opt) => (
              <Grid item key={opt.value}>
                <Box
                  onClick={() => handleChange("proficiency", opt.value)}
                  sx={{
                    width: 56,
                    height: 56,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    border: goal.proficiency === opt.value ? "2px solid #4a90e2" : "1px solid #e0e0e0",
                    backgroundColor: goal.proficiency === opt.value ? "#eef5fc" : "transparent",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <Typography variant="h6" color={goal.proficiency === opt.value ? "#4a90e2" : "textPrimary"}>
                    {opt.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      );
    }
    case 5: {
      return (
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={dateLocale}
          localeText={customLocaleText}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {t("AI-Coaching.goalWizard.form.timeRangeHelp")}
            </Typography>
            {weekdayTimes.map((range, index) => (
              <Box key={index} display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={1}>
                <Checkbox
                  checked={weekdayChecked[index]}
                  onChange={(e) => {
                    const updated = [...weekdayChecked];
                    updated[index] = e.target.checked;
                    setWeekdayChecked(updated);

                    const week = updated
                      .map((flag, i) => (flag ? weekdayTimes[i] : null))
                      .filter((v): v is [string, string] => v !== null);
                    handleChange("weekly_target_hours", week);
                  }}
                />
                <MobileTimePicker
                  label={t("AI-Coaching.goalWizard.form.startTime")}
                  value={parseTime(range[0] || "08:00")}
                  onChange={(val) => {
                    if (!val) return;
                    const updated = [...weekdayTimes];
                    updated[index][0] = formatTime(val);
                    setWeekdayTimes(updated);
                  }}
                  ampm={false}
                  slots={{ textField: TextField }}
                  slotProps={{
                    dialog: {
                      disableEnforceFocus: true,
                      disableAutoFocus: true,
                    },
                    textField: { sx: { minWidth: 140 } },
                  }}
                />
                <Typography>~</Typography>
                <MobileTimePicker
                  label={t("AI-Coaching.goalWizard.form.endTime")}
                  value={parseTime(range[1] || "09:00")}
                  onChange={(val) => {
                    if (!val) return;
                    const updated = [...weekdayTimes];
                    updated[index][1] = formatTime(val);
                    setWeekdayTimes(updated);
                  }}
                  ampm={false}
                  slots={{ textField: TextField }}
                  slotProps={{
                    dialog: {
                      disableEnforceFocus: true,
                      disableAutoFocus: true,
                    },
                    textField: { sx: { minWidth: 140 } },
                  }}
                />
              </Box>
            ))}
          </Box>
        </LocalizationProvider>
      );
    }
    case 6:
      return (
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={dateLocale}
          localeText={customLocaleText}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {t("AI-Coaching.goalWizard.form.holiday_timeRangeHelp")}
            </Typography>
            {weekendTimes.map((range, index) => (
              <Box key={index} display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={1}>
                <Checkbox
                  checked={weekendChecked[index]}
                  onChange={(e) => {
                    const updated = [...weekendChecked];
                    updated[index] = e.target.checked;
                    setWeekendChecked(updated);

                    const holiday = updated
                      .map((flag, i) => (flag ? weekendTimes[i] : null))
                      .filter((v): v is [string, string] => v !== null);
                    handleChange("holiday_weekly_target_hours", holiday);
                  }}
                />
                <MobileTimePicker
                  label={t("AI-Coaching.goalWizard.form.startTime")}
                  value={parseTime(range[0] || "08:00")}
                  onChange={(val) => {
                    if (!val) return;
                    const updated = [...weekendTimes];
                    updated[index][0] = formatTime(val);
                    setWeekendTimes(updated);
                  }}
                  ampm={false}
                  slots={{ textField: TextField }}
                  slotProps={{
                    dialog: {
                      disableEnforceFocus: true,
                      disableAutoFocus: true,
                    },
                    textField: { sx: { minWidth: 140 } },
                  }}
                />
                <Typography>~</Typography>
                <MobileTimePicker
                  label={t("AI-Coaching.goalWizard.form.endTime")}
                  value={parseTime(range[1] || "09:00")}
                  onChange={(val) => {
                    if (!val) return;
                    const updated = [...weekendTimes];
                    updated[index][1] = formatTime(val);
                    setWeekendTimes(updated);
                  }}
                  ampm={false}
                  slots={{ textField: TextField }}
                  slotProps={{
                    dialog: {
                      disableEnforceFocus: true,
                      disableAutoFocus: true,
                    },
                    textField: { sx: { minWidth: 140 } },
                  }}
                />
              </Box>
            ))}
          </Box>
        </LocalizationProvider>
      );
    case 7:
      return (
        <Box sx={{ width: "100%" }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {t("AI-Coaching.goalWizard.step.request")}
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            {t("AI-Coaching.goalWizard.form.requestHelp")}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={5}
            label={t("AI-Coaching.goalWizard.form.request")}
            value={goal.ai_request}
            onChange={(e) => handleChange("ai_request", e.target.value)}
          />
        </Box>
      );

    default:
      return <Typography>{t("AI-Coaching.goalWizard.stepComplete")}</Typography>;
  }
}

function GoalWizardScreen(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useContext(LanguageContext) as any;
  const { session } = useSessionContext();
  const { openPaywall } = usePaywall();

  // MUI DatePicker用の動的ロケール
  const dateLocale = language === "en" ? enUS : ja;
  const muiLocale = language === "en" ? enUSLocale : jaJP;

  const customLocaleText = {
    ...muiLocale.components.MuiLocalizationProvider.defaultProps.localeText,
    cancelButtonLabel: t("Common.cancel"),
    okButtonLabel: t("Common.ok"),
  };

  const steps = [
    t("AI-Coaching.goalWizard.step.category"),
    t("AI-Coaching.goalWizard.step.detail"),
    t("AI-Coaching.goalWizard.step.type"),
    t("AI-Coaching.goalWizard.step.motivation"),
    t("AI-Coaching.goalWizard.step.proficiency"),
    t("AI-Coaching.goalWizard.step.time"),
    t("AI-Coaching.goalWizard.step.holiday-time"),
    t("AI-Coaching.goalWizard.step.request"),
  ];
  const [activeStep, setActiveStep] = useState<number>(0);

  const [goal, setGoal] = useState(() => {
    const goalData = location.state?.goalData;

    return goalData
      ? {
          id: goalData.id,
          category: goalData.category || "",
          goal: goalData.goal || "",
          goal_type: goalData.goal_type || "",
          target_date: goalData.target_date ? new Date(goalData.target_date).toISOString().split("T")[0] : "",
          motivationtype: goalData.motivationtype || "",
          proficiency: goalData.proficiency ?? null,
          weekly_target_hours: goalData.weekly_target_hours || ([] as [string, string][]),
          holiday_weekly_target_hours: goalData.holiday_weekly_target_hours || ([] as [string, string][]),
          ai_request: goalData.ai_request || "",
        }
      : {
          id: 0,
          category: "",
          goal: "",
          goal_type: "",
          target_date: "",
          motivationtype: "",
          proficiency: null,
          weekly_target_hours: [] as [string, string][],
          holiday_weekly_target_hours: [] as [string, string][],
          ai_request: "",
        };
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const isLastStep: boolean = activeStep === steps.length - 1;

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [weekdayTimes, setWeekdayTimes] = useState<[string, string | null][]>(() => generateDefaultTimes());
  const [weekendTimes, setWeekendTimes] = useState<[string, string | null][]>(() => generateDefaultTimes());

  const [weekdayChecked, setWeekdayChecked] = useState<boolean[]>(() => Array(7).fill(false));
  const [weekendChecked, setWeekendChecked] = useState<boolean[]>(() => Array(7).fill(false));
  const [goalCount, setGoalCount] = useState<number>(0);
  const [isMaxGoalsReached, setIsMaxGoalsReached] = useState<boolean>(false);
  const [userPlan, setUserPlan] = useState<"Free" | "Pro">("Free");
  const [isCheckingLimit, setIsCheckingLimit] = useState<boolean>(true);
  const [aiRemainingCount, setAiRemainingCount] = useState<number | null>(null);

  const checkLimits = useCallback(async () => {
    if (!session?.access_token) return;
    
    setIsCheckingLimit(true);
    try {
      const count = await getUserGoalCount(session.access_token);
      setGoalCount(count);

      let isPro = false;
      try {
        const userInfo = await getUserInfos(session.access_token);
        if (userInfo.plan_type === "Pro" && userInfo.subscription_expires_at) {
          const expiresAt = new Date(userInfo.subscription_expires_at).getTime();
          if (expiresAt > Date.now()) {
            isPro = true;
          }
        }
      } catch (err) {
        console.error("UserInfo取得エラー:", err);
      }

      // AIプランのステータス(残回数)を取得
      try {
        const status = await getAiPlanStatus(session.access_token);
        if (status) {
          setAiRemainingCount(status.remainingCount);
          // 万が一userInfoと異なる場合、より確実なこちらのプラン情報を優先
          isPro = status.planType === "Pro";
        }
      } catch (err) {
        console.error("AI Plan Status取得エラー:", err);
      }

      const currentPlan = isPro ? "Pro" : "Free";
      setUserPlan(currentPlan);

      const goalData = location.state?.goalData;
      // 編集画面ではなく新規作成の場合のみ上限を判定する
      if (!goalData) {
        const maxAllowed = currentPlan === "Pro" ? 3 : 1;
        setIsMaxGoalsReached(count >= maxAllowed);
      } else {
        setIsMaxGoalsReached(false);
      }
    } catch (e) {
      console.error("プラン数の取得に失敗", e);
    } finally {
      setIsCheckingLimit(false);
    }
  }, [session, location.state]);

  useEffect(() => {
    checkLimits();
  }, [checkLimits]);
  
  useEffect(() => {
    const goalData = location.state?.goalData;
    if (goalData) {
      if (goalData.weekly_target_hours && goalData.weekly_target_hours.length > 0) {
        const newWeekdayTimes: [string, string][] = generateDefaultTimes();
        const newWeekdayChecked: boolean[] = Array(7).fill(false);
        goalData.weekly_target_hours.forEach((timeRange: [string, string], index: number) => {
          if (index < 7) {
            newWeekdayTimes[index] = timeRange;
            newWeekdayChecked[index] = true;
          }
        });
        setWeekdayTimes(newWeekdayTimes);
        setWeekdayChecked(newWeekdayChecked);
      } else {
        setWeekdayTimes(generateDefaultTimes());
        setWeekdayChecked(Array(7).fill(false));
      }

      if (goalData.holiday_weekly_target_hours && goalData.holiday_weekly_target_hours.length > 0) {
        const newWeekendTimes: [string, string][] = generateDefaultTimes();
        const newWeekendChecked: boolean[] = Array(7).fill(false);
        goalData.holiday_weekly_target_hours.forEach((timeRange: [string, string], index: number) => {
          if (index < 7) {
            newWeekendTimes[index] = timeRange;
            newWeekendChecked[index] = true;
          }
        });
        setWeekendTimes(newWeekendTimes);
        setWeekendChecked(newWeekendChecked);
      } else {
        setWeekendTimes(generateDefaultTimes());
        setWeekendChecked(Array(7).fill(false));
      }
    }
  }, [location.state]);

  const categoryOptions = useMemo(
    () => [
      { label: t("AI-Coaching.goalWizard.categories.english"), value: "english", image: "/images/category/english.png" },
      { label: t("AI-Coaching.goalWizard.categories.exam"), value: "exam", image: "/images/category/exam.png" },
      { label: t("AI-Coaching.goalWizard.categories.license"), value: "license", image: "/images/category/license.png" },
      { label: t("AI-Coaching.goalWizard.categories.diet"), value: "diet", image: "/images/category/diet.png" },
      { label: t("AI-Coaching.goalWizard.categories.exerciseHabits"), value: "exerciseHabits", image: "/images/category/exerciseHabits.png" },
      { label: t("AI-Coaching.goalWizard.categories.programming"), value: "programming", image: "/images/category/programming.png" },
      { label: t("AI-Coaching.goalWizard.categories.habituation"), value: "habituation", image: "/images/category/habituation.png" },
      { label: t("AI-Coaching.goalWizard.categories.other"), value: "other", image: "/images/category/other.png" },
    ],
    [t]
  );

  const goalTypeOptions = useMemo(
    () => [
      { label: t("AI-Coaching.goalWizard.options.deadline"), value: "deadline" },
      { label: t("AI-Coaching.goalWizard.options.habit"), value: "habit" },
    ],
    [t]
  );

  const motivationOptions = useMemo(
    () => [
      { label: t("AI-Coaching.goalWizard.options.motivation1"), value: "motivation1" },
      { label: t("AI-Coaching.goalWizard.options.motivation2"), value: "motivation2" },
      { label: t("AI-Coaching.goalWizard.options.motivation3"), value: "motivation3" },
      { label: t("AI-Coaching.goalWizard.options.motivation4"), value: "motivation4" }
    ],
    [t]
  );

  const proficiencyOptions = useMemo(
    () => Array.from({ length: 11 }, (_, i) => ({
      label: `${i}`,
      value: i,
    })),
    []
  );

  const showError = (key: string) => {
    setErrorMessage("");
    setTimeout(() => {
      setErrorMessage(t(`AI-Coaching.goalWizard.error.${key}`));
    }, 0);
  };

  const handleNext = () => {
    setErrorMessage("");
    switch (activeStep) {
      case 0:
        if (!goal.category) return showError("category");
        break;
      case 1:
        if (!goal.goal) return showError("goal");
        break;
      case 2:
        if (!goal.goal_type) return showError("goalType");
        if (goal.goal_type === "deadline" && !goal.target_date) return showError("target_date");
        break;
      case 3:
        if (!goal.motivationtype) return showError("motivationtype");
        break;
      case 4:
        if (goal.proficiency === null) return showError("proficiency");
        break;
      case 5:
        const weekdayFiltered = weekdayChecked
          .map((flag, i) => (flag ? weekdayTimes[i] : null))
          .filter((v): v is [string, string] => v !== null);

        const weekendFiltered = weekendChecked
          .map((flag, i) => (flag ? weekendTimes[i] : null))
          .filter((v): v is [string, string] => v !== null);

        if (weekdayFiltered.length === 0 && weekendFiltered.length === 0) {
          return showError("time");
        }
        break;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setErrorMessage("");
    setActiveStep((prev) => prev - 1);
  };

  const handleChange = (key: string, value: any) => {
    setGoal({ ...goal, [key]: value });
  };

  const handleSubmit = async () => {
    if (!goal.category) return showError("category");
    if (!goal.goal) return showError("goal");
    if (!goal.goal_type) return showError("goalType");
    if (goal.goal_type === "deadline" && !goal.target_date) return showError("target_date");
    if (!goal.motivationtype) return showError("motivationtype");
    if (goal.proficiency === null) return showError("proficiency");

    const weekdayFiltered = weekdayChecked
      .map((flag, i) => (flag ? weekdayTimes[i] : null))
      .filter((v): v is [string, string] => v !== null);

    const weekendFiltered = weekendChecked
      .map((flag, i) => (flag ? weekendTimes[i] : null))
      .filter((v): v is [string, string] => v !== null);

    if (weekdayFiltered.length === 0 && weekendFiltered.length === 0) {
      return showError("time");
    }

    try {
      const payload = {
        ...goal,
        weekly_target_hours: weekdayFiltered,
        holiday_weekly_target_hours: weekendFiltered,
      };
      
      // idの有無で 新規作成(registGoal) と 編集(updateGoal) を切り替える
      let res;
      if (goal.id && goal.id !== 0) {
        res = await updateGoal(session.access_token, payload);
      } else {
        res = await registGoal(session.access_token, payload);
      }

      console.log("AIプラン送信完了", res);
      setIsSubmitted(true);
    } catch (err: any) {
      console.error("送信失敗", err);
      // バックエンド側で回数超過エラー(403)が返された場合の表示
      if (err.response && err.response.status === 403) {
        setErrorMessage(err.response.data?.error || "AIプランの作成上限に達しています。");
      } else {
        showError("submitFail");
      }
    }
  };

  const progressPercentage = ((activeStep + 1) / steps.length) * 100;
  
  // Submitボタンを非活性にするかどうかの判定
  const isSubmitDisabled = isLastStep && aiRemainingCount === 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container justifyContent="center" sx={{ my: 4 }}>
          <Grid item xs={12} lg={8} xl={6}>
            <MDBox sx={{ mt: 0, pt: 0, mb: 4 }} textAlign="center">
              {!isSubmitted && (
                <MDBox sx={{ mt: 0, pt: 0, mb: 4 }} textAlign="center">
                  <MDBox mb={1} display="flex" justifyContent="center" alignItems="center" gap={2}>
                    <MDTypography variant="h5" fontWeight="bold">
                      {t("AI-Coaching.goalWizard.title")}
                    </MDTypography>
                    <MDTypography variant="body1" color="text" fontWeight="medium">
                      {goalCount}{t("AI-Coaching.goalWizard.plan-title")}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="h6" fontWeight="regular" color="secondary">
                    {t("AI-Coaching.goalWizard.subtitle")}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>

            <Card sx={{ overflow: "visible" }}>
              {isCheckingLimit ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  {/* グローバルインジケーターが表示されるため、ここは空にしてレイアウトシフトのみ防ぐ */}
                </MDBox>
              ) : isSubmitted ? (
                <MDBox textAlign="center" px={{ xs: 2, sm: 5 }} py={{ xs: 3, sm: 5 }} sx={{ background: "linear-gradient(145deg, #ffffff, #f5f8fe)" }}>
                  <MDBox display="inline-flex" alignItems="center" gap={0.75} px={1.5} py={0.75} mb={2} sx={{ border: "1px solid #cfe1ff", borderRadius: 99, color: "#2367db", backgroundColor: "#ffffff" }}>
                    <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: "inherit", letterSpacing: ".06em" }}>
                      {t("AI-Coaching.goalWizard.result.eyebrow")}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="h3" fontWeight="bold" sx={{ color: "#102957", mb: 1 }}>
                    {t("AI-Coaching.goalWizard.result.headline")}
                  </MDTypography>
                  <MDTypography variant="body1" color="secondary" mb={3}>
                    {t("AI-Coaching.goalWizard.result.description")}
                  </MDTypography>
                  <MDBox sx={{ width: 132, height: 132, borderRadius: "50%", backgroundColor: "#eef5ff", border: "1px solid #d6e5fc", display: "grid", placeItems: "center", mx: "auto", mb: 2 }}>
                    <MDBox sx={{ width: 78, height: 78, borderRadius: 3, backgroundColor: "#2f76e8", border: "7px solid #e6f0ff", display: "grid", placeItems: "center", boxShadow: "0 12px 24px rgba(47,118,232,.25)" }}>
                      <AutoAwesomeIcon sx={{ color: "#ffffff", fontSize: 38 }} />
                    </MDBox>
                  </MDBox>
                  <MDBox display="flex" justifyContent="center" alignItems="center" gap={1} mb={3}>
                    <CircularProgress size={17} color="info" />
                    <MDTypography variant="button" fontWeight="bold" color="info">
                      {t("AI-Coaching.goalWizard.result.progressLabel")}
                    </MDTypography>
                  </MDBox>

                  <MDBox sx={{ maxWidth: 620, mx: "auto", mb: 3, p: 1.5, border: "1px solid #dce8fa", borderRadius: 3, backgroundColor: "#ffffff", textAlign: "left" }}>
                    {[
                      [<CheckIcon />, "goal", "#2f76e8", "#2f76e8"],
                      [<AutoAwesomeIcon />, "plan", "#e7f0ff", "#2874ea"],
                      [<EventNoteIcon />, "review", "#f0f3f8", "#9aaac4"],
                    ].map(([icon, step, background, color], index) => (
                      <MDBox key={String(step)} display="flex" alignItems="center" py={1.25} sx={{ borderBottom: index < 2 ? "1px solid #edf1f7" : "none" }}>
                        <MDBox sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: background as string, color: color as string, display: "grid", placeItems: "center", mr: 1.25 }}>
                          {icon as ReactElement}
                        </MDBox>
                        <MDBox flex={1}>
                          <MDTypography variant="button" fontWeight="bold" sx={{ color: "#1b315b", display: "block" }}>
                            {t(`AI-Coaching.goalWizard.result.steps.${step}.title`)}
                          </MDTypography>
                          <MDTypography variant="caption" color="secondary">
                            {t(`AI-Coaching.goalWizard.result.steps.${step}.caption`)}
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: color as string }}>
                          {t(`AI-Coaching.goalWizard.result.steps.${step}.state`)}
                        </MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>

                  <MDButton
                    color="info"
                    variant="gradient"
                    onClick={() => navigate("/pages/ai-coaching/AIPlan", { state: { isCreatingPlan: true } })}
                    endIcon={<ArrowForwardIcon />}
                  >
                    {t("AI-Coaching.goalWizard.buttons.viewPlan")}
                  </MDButton>
                </MDBox>
              ) : isMaxGoalsReached && !location.state?.goalData ? (
                <MDBox p={4} display="flex" flexDirection="column" alignItems="center" minHeight="300px" justifyContent="center">
                  <MDBox
                    display="flex"
                    alignItems="center"
                    p={2}
                    sx={{
                      backgroundColor: "#FFEBEE",
                      border: "1px solid #FFCDD2",
                      borderRadius: "8px",
                      mb: 3,
                      width: "100%",
                      maxWidth: "500px"
                    }}
                  >
                    <Icon sx={{ color: "#d32f2f", mr: 1 }}>error_outline</Icon>
                    <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#d32f2f" }}>
                      {userPlan === "Free"
                        ? t("AI-Coaching.max_err_free") || "無料プランでは目標は1つまでです。Proプランへアップグレードしてさらに目標を追加しましょう！"
                        : t("AI-Coaching.max_err") || "設定できる目標の最大数（3つ）に達しました。"}
                    </MDTypography>
                  </MDBox>
                  
                  {userPlan === "Free" && (
                    <MDButton
                      variant="contained"
                      color="info"
                      size="large"
                      onClick={openPaywall}
                      sx={{
                        borderRadius: "30px",
                        px: 4,
                        py: 1.5,
                        fontSize: "16px",
                        boxShadow: "0px 4px 10px rgba(26, 115, 232, 0.3)"
                      }}
                    >
                      {t("menu.upgrade_pro") || "Proプランへアップグレード"}
                    </MDButton>
                  )}
                </MDBox>
              ) : (
                /* 既存のフォームUI */
                <>
                  <MDBox p={3} pb={0}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {t("tabs.aiPlan.goal")}
                    </Typography>
                    <Box sx={{ width: "100%", mt: 2, mb: 1 }}>
                      <Box sx={{ width: '100%', height: 8, backgroundColor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                        <Box 
                          sx={{ 
                            width: `${progressPercentage}%`, 
                            height: '100%', 
                            backgroundColor: '#4a90e2', 
                            transition: 'width 0.3s ease' 
                          }} 
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary" align="right" sx={{ mt: 1 }}>
                        Step {activeStep + 1} / {steps.length}
                      </Typography>
                    </Box>
                  </MDBox>
                  
                  <MDBox p={3}>
                    <MDBox>
                      {errorMessage && (
                        <MDAlert color="error" dismissible>
                          {errorMessage}
                        </MDAlert>
                      )}
                      
                      <Box sx={{ minHeight: isLastStep ? 0 : 300, py: 2, mb: isLastStep ? 0 : 2 }}>
                        {getStepContent(
                          activeStep,
                          goal,
                          handleChange,
                          weekdayTimes,
                          setWeekdayTimes,
                          weekendTimes,
                          setWeekendTimes,
                          weekdayChecked,
                          setWeekdayChecked,
                          weekendChecked,
                          setWeekendChecked,
                          t,
                          categoryOptions,
                          goalTypeOptions,
                          motivationOptions,
                          proficiencyOptions,
                          dateLocale,
                          customLocaleText
                        )}
                      </Box>

                      {/* AIプラン作成残回数の表示 */}
                      <MDBox mt={2}>
                        {isLastStep && aiRemainingCount !== null && (
                          <MDBox
                            p={3}
                            mb={3}
                            sx={{
                              backgroundColor: aiRemainingCount === 0 ? "#FFEBEE" : "#eef5fc",
                              border: `1px solid ${aiRemainingCount === 0 ? "#FFCDD2" : "#cde0f5"}`,
                              borderRadius: "8px",
                              textAlign: "center",
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              fontWeight="bold" 
                              color={aiRemainingCount === 0 ? "error" : "textPrimary"}
                              mb={aiRemainingCount === 0 ? 1 : 0}
                            >
                              {userPlan === "Free" 
                                ? (t("AI-Coaching.goalWizard.limits.freeRemaining") || "Freeプランでは、あと {count} 回AIプランを作成できます").replace("{count}", String(aiRemainingCount))
                                : (t("AI-Coaching.goalWizard.limits.proRemaining") || "今月のAIプラン作成可能回数：{count} 回").replace("{count}", String(aiRemainingCount))}
                            </Typography>
                            
                            {/* Freeプランで上限到達時の表示とアップグレードボタン */}
                            {aiRemainingCount === 0 && userPlan === "Free" && (
                              <>
                                <Typography variant="body2" fontWeight="bold" color="error" display="block">
                                  {t("AI-Coaching.goalWizard.limits.freeEmpty")}
                                </Typography>
                                <MDBox mt={3} display="flex" justifyContent="center">
                                  <MDButton
                                    variant="contained"
                                    color="info"
                                    size="large"
                                    onClick={openPaywall}
                                    sx={{
                                      borderRadius: "30px",
                                      px: 4,
                                      py: 1.5,
                                      fontSize: "16px",
                                      boxShadow: "0px 4px 10px rgba(26, 115, 232, 0.3)"
                                    }}
                                  >
                                    {t("menu.upgrade_pro")}
                                  </MDButton>
                                </MDBox>
                              </>
                            )}
                            
                            {/* Proプランで上限到達時の表示 */}
                            {aiRemainingCount === 0 && userPlan === "Pro" && (
                              <Typography variant="body2" fontWeight="bold" color="error" display="block" mt={1}>
                                {t("AI-Coaching.goalWizard.limits.proEmpty")}
                              </Typography>
                            )}
                          </MDBox>
                        )}
                      </MDBox>

                      <MDBox width="100%" display="flex" gap={2}>
                        {activeStep !== 0 && (
                          <MDButton 
                            variant="outlined" 
                            color="dark" 
                            onClick={handleBack}
                            fullWidth
                            sx={{ py: 1.5 }}
                          >
                            {t("AI-Coaching.goalWizard.buttons.back")}
                          </MDButton>
                        )}
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={!isLastStep ? handleNext : handleSubmit}
                          fullWidth
                          disabled={isSubmitDisabled}
                          sx={{ 
                            py: 1.5, 
                            backgroundColor: isSubmitDisabled ? "#e0e0e0" : "#4a90e2" 
                          }}
                        >
                          {isLastStep ? t("AI-Coaching.goalWizard.buttons.submit") : t("AI-Coaching.goalWizard.buttons.next")}
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default GoalWizardScreen;
